import { Headphones, Mic, MicOff, Users } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { Badge } from '../../components/ui/badge';
import { cn } from '../../lib/utils';
import { User } from '../../models/User';
import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import { twil } from '../../services/TwilioHelper';
import { UserAvatar } from './UserAvatar';
import UserPopOver from './UserPopover';

export const ParticipantItem: React.FC<{ html: HTMLElement }> = ({ html }) => {
  const htmlElement = useRef<HTMLDivElement>(null);
  const [user, setUser] = useState<User>();
  const [isMuted, setIsMuted] = useState<boolean>();
  const [isDominant, setIsDominant] = useState<boolean>();
  const [fullScreen, setFullscreen] = useState(false);
  const isLocalUser = html.id == My.user.id;

  useEffect(() => {

    if (isLocalUser) {
      setUser(My.user)
    } else {
      Fire.getUser(html.id)
        .then(u => {
          setUser(u);
          let remotePart = twil.remoteParticipants.get(html.id);

          if (remotePart) {
            let enabledAudioTrack = Array.from(remotePart?.audioTracks.values() ?? []).find(track => track.isTrackEnabled);
            setIsMuted(!enabledAudioTrack);
            setIsDominant(enabledAudioTrack && twil.twilioRoom?.dominantSpeaker?.identity === html.id);
          }

          setTimeout(() => {
            htmlElement.current?.appendChild(html)
          }, 100);;
        });
    }

    return twil.subscribeToTwilioUpdates((e) => {
      if (isLocalUser)
        setIsMuted(!twil.isMicEnabled());
      else if (e?.type == 'UserMuteChanged' && e.userId === html.id)
        setIsMuted(e?.value);

      if (e?.type === 'DominantSpeakerChanged') {
        setIsDominant(!isMuted && e.userId === html.id);
      }
    });
  }, [])

  if (!user) return null;

  return (
    <div
      className={cn(
        "relative rounded-lg overflow-hidden bg-black/60 transition-all duration-300 isolate group shadow-lg ring-1 ring-white/10",
        fullScreen ? "fixed inset-0 z-[100] w-full h-full rounded-none" : "w-[120px] aspect-[4/3] sm:w-[160px]",
        isDominant && !isMuted ? "ring-2 ring-green-500 shadow-green-500/20" : ""
      )}
    >
      <div
        ref={htmlElement}
        className="w-full h-full [&>video]:w-full [&>video]:h-full [&>video]:object-cover cursor-pointer"
        onClick={() => setFullscreen(p => !p)}
      />

      {/* User Info Overlay */}
      <div className={cn(
        "absolute bottom-0 left-0 right-0 p-1.5 bg-gradient-to-t from-black/90 via-black/50 to-transparent flex items-center justify-between",
        fullScreen && "p-4"
      )}>
        <UserPopOver user={user} content={
          <div className="flex items-center gap-1.5 max-w-full cursor-pointer hover:bg-white/10 rounded p-0.5 transition-colors">
            <UserAvatar src={user.photoUrl} name={user.name} className={cn("w-5 h-5 ring-1 ring-white/20", fullScreen && "w-8 h-8")} fallbackType="text" />
            <span className={cn("text-[10px] text-white font-medium truncate drop-shadow-md", fullScreen && "text-sm")}>{user.name}</span>
          </div>
        } />

        <div className="flex items-center justify-center w-5 h-5 rounded-full bg-black/40 backdrop-blur-sm shadow-sm ring-1 ring-white/10">
          {isMuted ? <MicOff className="w-3 h-3 text-red-400" /> : <Mic className={cn("w-3 h-3 text-white", isDominant && "text-green-400")} />}
        </div>
      </div>

      {/* Screen share indicator for local user */}
      {user.id === My.user.id && twil.isScreenSharing() && (
        <div className="absolute top-1 right-1 w-2 h-2 bg-blue-500 rounded-full animate-pulse shadow-lg ring-2 ring-black/50" title="Sharing Screen" />
      )}
    </div>
  );
}

export const CallTitleBadge = (roomName: string, nrOfParticipants: number, disabled: boolean) => {
  return (
    <div className='flex items-center gap-3'>
      {roomName && <span className='font-semibold text-lg truncate max-w-[200px]' title={roomName}>{roomName}</span>}
      {MiniCallBadge(nrOfParticipants, disabled)}
    </div>
  );
}

export const MiniCallBadge = (nrOfCallUsers: number, disabled: boolean) => {
  return (
    <Badge
      variant="secondary"
      className={cn(
        "flex items-center gap-1.5 px-2 py-0.5 h-6 shrink-0 transition-opacity",
        disabled ? "bg-muted text-muted-foreground opacity-70" : "bg-emerald-200 text-emerald-800 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 dark:border-emerald-800",
        "border"
      )}
    >
      <Headphones className="w-3 h-3" />
      <span className="text-xs font-medium tabular-nums">{nrOfCallUsers}</span>
    </Badge>
  );
}